import LeadingLinks from '../../components/leading-links';
import AsideNavLink from '../../components/aside-nav-link';
import AsideLayout from '../../components/layouts/aside-layout';
import MainLayout from '../../components/layouts/main-layout';
import HeroImage from '../../public/img/hero/money3_01.webp';
import Money3Logo from '../../public/img/logos/money3_02.svg';
import Image from 'next/image';
import Head from 'next/head';
import Link from 'next/link';
import Script from 'next/script';

export default function Money3() {
    return (
        <MainLayout>
            <Head>
                <title>Money3 Loans Pty Ltd | Consumer Finance Brand of Solvar Limited</title>
                <meta name="description" content="Money3 Loans Pty Ltd is an Australian non-bank lender and a wholly owned subsidiary of Solvar Limited (ASX: SVR), providing vehicle finance and personal loan solutions across Australia. "></meta>
                <script
                    key="schema"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@graph": [
                                {
                                    "@type": "AboutPage",
                                    "@id": "https://www.solvar.com.au/brands/money3/#webpage",
                                    "url": "https://www.solvar.com.au/brands/money3/",
                                    "name": "Money3 Loans Pty Ltd",
                                    "description": "Overview of Money3 Loans Pty Ltd, an Australian non-bank lender and subsidiary of Solvar Limited.",
                                    "isPartOf": {
                                        "@id": "https://www.solvar.com.au/#website"
                                    },
                                    "about": {
                                        "@id": "https://www.money3.com.au/#organisation"
                                    },
                                    "publisher": {
                                        "@id": "https://www.solvar.com.au/#organisation"
                                    }
                                },
                                {
                                    "@type": "WebSite",
                                    "@id": "https://www.solvar.com.au/#website",
                                    "url": "https://www.solvar.com.au/",
                                    "name": "Solvar Limited",
                                    "publisher": {
                                        "@id": "https://www.solvar.com.au/#organisation"
                                    }
                                },
                                {
                                    "@type": "Corporation",
                                    "@id": "https://www.solvar.com.au/#organisation",
                                    "name": "Solvar Limited",
                                    "url": "https://www.solvar.com.au/",
                                    "tickerSymbol": "ASX:SVR",
                                    "subOrganization": {
                                        "@id": "https://www.money3.com.au/#organisation"
                                    }
                                },
                                {
                                    "@type": "FinancialService",
                                    "@id": "https://www.money3.com.au/#organisation",
                                    "name": "Money3 Loans Pty Ltd",
                                    "alternateName": "Money3",
                                    "url": "https://www.money3.com.au/",
                                    "logo": "https://media.umbraco.io/solvar-web-projects/dpni4nxy/logo.svg",
                                    "slogan": "We'll give you a fair go!",
                                    "parentOrganization": {
                                        "@id": "https://www.solvar.com.au/#organisation"
                                    },
                                    "memberOf": [
                                        {
                                            "@type": "Organization",
                                            "name": "Australian Financial Complaints Authority"
                                        }
                                    ],
                                    "areaServed": {
                                        "@type": "Country",
                                        "name": "Australia"
                                    },
                                    "knowsAbout": [
                                        "Vehicle Finance",
                                        "Personal Loans",
                                        "Consumer Lending",
                                        "Non-bank Lending"
                                    ]
                                }
                            ]

                        })
                    }}
                />
            </Head>
            <AsideLayout
                prev={ null }
                next={ null }
                heroImage={ HeroImage }
                location={
                    <p className="capitalize text-bb">
                        <span className="font-bold">Our Brands</span> › Money3
                    </p>
                }
                aside={
                    <>
                        <h6 className="text-h3 text-bb pb-20">Our Brands</h6>
                        <AsideNavLink href="https://www.afs.com.au/" passHref>Automotive Financial Services</AsideNavLink>
                        <AsideNavLink href="https://www.gocar.co.nz/" passHref>Go Car Finance</AsideNavLink>
                        <AsideNavLink href="/brands/money3" active={ true }>Money3</AsideNavLink>
                        <AsideNavLink href="https://bennji.com.au/" passHref>Bennji</AsideNavLink>
                    </>
                }
            >
                <div className="md:flex md:justify-between md:items-center">
                    <h1 className="text-bb pb-40 md:pb-0 md:leading-none md:grow-1">Money3 Loans Pty Ltd</h1>
                    <Link className="block w-fit mx-auto md:mx-0 text-center md:text-end no-underline" href="https://www.money3.com.au/" target="_blank" passHref>
                        <Image className="max-w-[180px] inline-block" src={ Money3Logo } alt="Money3 logo" layout="responsive" />
                        <p className="text-bb font-bold">www.money3.com.au</p>
                    </Link>
                </div>
                <div className="sol-markdown-content">
                    <h2>Overview</h2>
                    <p>Money3 Loans Pty Ltd (commonly known as Money3) is an Australian non-bank lender specialising in secured and unsecured consumer finance. The company operates as a subsidiary of Solvar Limited (ASX: SVR), a publicly listed financial services group.</p>
                    <p>
                        <Link href="https://www.money3.com.au" passHref>Money3</Link> provides lending solutions to retail customers across Australia, with a primary focus on asset-backed finance and personal lending. Its products are used by customers to access vehicles, personal transport, and other lifestyle or essential assets. The company is commonly associated with the phrase "We'll give you a fair go!", which reflects its approach to assessing applications on their individual merits, including for customers with non-standard credit histories or income sources.
                    </p>
                    <h2>Business Overview</h2>
                    <p>Money3 operates within the Australian non-bank lending sector, providing finance to customers who may not meet traditional bank lending criteria. The business focuses on responsible lending practices and structured repayment solutions aligned with customer income.</p>
                    <p>The company's core activities include:</p>
                    <ul>
                        <li>Secured lending for vehicles and other personal-use assets</li>
                        <li>Unsecured personal loans for a range of consumer purposes</li>
                        <li>Loan servicing and account management across the full loan lifecycle</li>
                    </ul>
                    <p>Money3 distributes its products through a combination of broker networks, referral partners, and direct digital channels.</p>
                    <h2>History</h2>
                    <p>Money3 traces its origins to the early 2000s, when the business commenced trading under the name Money Plus.</p>
                    <p>In 2005, the company adopted the name Money3 Corporation Limited and was listed on the Australian Securities Exchange in 2006.</p>
                    <p>In 2013, the company relocated its head office and secured loan operations to Bundoora, Melbourne.</p>
                    <p>In 2015, Scott Baldwin was appointed Chief Executive Officer.</p>
                    <p>In 2017, Money3 established a secured funding facility with Fortress Investment Group. In 2018, the company was added to the S&P/ASX 300 index.</p>
                    <p>In 2022, Money3 Corporation Limited changed its name to Solvar Limited, with Money3 continuing as a core consumer finance brand within the group.</p>
                    <h2>Products and Services</h2>
                    <h3>Secured Finance</h3>
                    <p>Money3 provides secured finance for a range of personal-use assets. While automotive lending remains a core focus, secured finance also extends to other asset classes including:</p>
                    <ul>
                        <li>Cars and other passenger vehicles (new and used)</li>
                        <li>Motorcycles and scooters </li>
                        <li>Caravans and camper trailers </li>
                        <li>Boats and personal watercraft </li>
                    </ul>
                    <p>Loans are typically secured against the asset being financed, with repayment structures aligned to customer affordability.</p>
                    <h3>Personal Loans</h3>
                    <p>Money3 offers unsecured personal loans to assist with a variety of consumer needs, including:</p>
                    <ul>
                        <li>Car repairs and maintenance </li>
                        <li>Medical costs </li>
                        <li>Lifestyle and personal events</li>
                        <li>Other personal expenses</li>
                    </ul>
                    <p>Loan terms and repayment schedules are structured based on individual customer circumstances and income profiles.</p>
                    <h2>Operations</h2>
                    <p>Money3 operates across Australia, with lending distributed through a network of brokers, referral partners, and direct digital channels.</p>
                    <p>The business manages the full loan lifecycle internally, including:</p>
                    <ul>
                        <li>credit assessment and decisioning</li>
                        <li>loan origination </li>
                        <li>account servicing and collections </li>
                    </ul>
                    <p>Funding for lending activities is supported through a combination of warehouse facilities, securitisation structures, and group-level capital management within Solvar Limited.</p>
                    <h2>Corporate Structure</h2>
                    <p>Money3 is a wholly owned subsidiary of Solvar Limited and forms part of the group's consumer finance operations and represents a core consumer lending platform of the group in Australia.</p>
                    <h2>Regulation and Compliance</h2>
                    <p>Money3 operates within the Australian financial services regulatory framework applicable to non-bank lenders.</p>
                    <p>Its lending activities are subject to oversight by the Australian Securities and Investments Commission (ASIC) and are governed by the National Consumer Credit Protection Act 2009. The company is also a member of the Australian Financial Complaints Authority (AFCA), which provides external dispute resolution for financial services complaints.</p>
                    <p>As part of a publicly listed group, Money3 operates within governance and compliance frameworks aligned with Australian Securities Exchange listing rules, including continuous disclosure obligations.</p>
                </div>
            </AsideLayout>
        </MainLayout>
    );
}